import dayjs from "dayjs";
import * as XLSX from "xlsx";
import request, { _POST, requestExport } from "@/services/request";

const BASE = "/admin/sample";

/** 1-待发货 2-进行中 3-履约终止 4-已完成 */
export type SampleApplyStatus = 1 | 2 | 3 | 4;

export const SampleApplyStatusMap = new Map<SampleApplyStatus, string>([
  [1, "待发货"],
  [2, "进行中"],
  [3, "履约中止"],
  [4, "完成"],
]);

export type SampleUserInfo = {
  headImg?: string;
  name?: string;
};

export type SampleShopInfo = {
  headImg?: string;
  name?: string;
};

export type SampleSelectionInfo = {
  selectionId?: string;
  selectionName?: string;
  headImg?: string;
};

export type SampleSkuAttr = {
  skuId?: string;
  specification?: string;
  price?: number;
};

export type SampleProductInfo = {
  productId?: string;
  name?: string;
  headImg?: string;
  minPrice?: number;
  maxPrice?: number;
  skuAttrKeys?: SampleSkuAttr[];
  shopInfo?: SampleShopInfo;
  selectionInfo?: SampleSelectionInfo;
  selectionName?: string;
  selectionHeadImg?: string;
  privateServiceRatio?: number;
};

export type SampleReceiverInfo = {
  name?: string;
  telNumber?: string;
  address?: string;
  postalCode?: string;
};

export type SampleFulfillmentInfo = {
  thresholdSettlementOrder?: string;
  thresholdLive?: string;
  thresholdVideo?: string;
};

export type TerminationFeedbackAttachment = {
  url?: string;
  fileUrl?: string;
  filePath?: string;
  thumbUrl?: string;
};

export type TerminationFeedbackInfo = {
  content?: string;
  attachments?: TerminationFeedbackAttachment[];
  submittedAt?: string;
};

/** 操作日志项（/admin/operation/log/list） */
export type SampleOperationLogItem = {
  operator?: string;
  description?: string;
  /** Unix 时间戳，秒 */
  createAt?: string;
};

/** 寄样申请列表项（与后端字段一致） */
export type SampleApplyListItem = {
  sampleApplyId?: string;
  productName?: string;
  productId?: string;
  skuAttrKeys?: SampleSkuAttr[];
  applyUser?: SampleUserInfo;
  shopInfo?: SampleShopInfo;
  selectionName?: string;
  /** 选品信息（含选品名称、头像等） */
  selectionInfo?: SampleSelectionInfo;
  applyThresholdSalesRevenueIn30Days?: string;
  applyThresholdTalentFansNumber?: string;
  fulfillment?: SampleFulfillmentInfo;
  address?: string;
  createdAt?: string;
  status?: number;
  /** 商品主图（第一张） */
  productHeadImg?: string;
  /** 最新操作日志 */
  latestOptLog?: SampleOperationLogItem;
};

/** 寄样申请详情（与后端字段一致） */
export type SampleApplyDetail = {
  sampleApplyId?: string;
  product?: SampleProductInfo;
  applyUser?: SampleUserInfo;
  receiver?: SampleReceiverInfo;
  fulfillment?: SampleFulfillmentInfo;
  applyThresholdSalesRevenueIn30Days?: string;
  applyThresholdTalentFansNumber?: string;
  remark?: string;
  trackingNo?: string;
  expressCompany?: string;
  /** 该达人待履约寄样申请数（待发货和进行中） */
  pendingFulfillmentCount?: string;
  /** 该达人履约中止寄样申请数 */
  terminatedFulfillmentCount?: string;
  /** 已完成的履约方式：1-直播履约，2-短视频履约，3-结算订单 */
  completedFulfillmentMethods?: SampleCompletedFulfillmentMethod[];
  /** 履约终止时的用户反馈 */
  terminationFeedback?: TerminationFeedbackInfo;
  createdAt?: string;
  completedAt?: string;
  status?: number;
};

export type SampleApplyOrder = {
  orderId?: string;
  createdAt?: string;
  amount?: string;
  estimatedCommission?: string;
  settlementStatus?: number;
  completedAt?: string;
};

export type SampleApplyListReq = {
  page?: number;
  size?: number;
  sampleApplyId?: string;
  productInfo?: string;
  status?: SampleApplyStatus;
  startTime?: string;
  endTime?: string;
  selectionInfo?: string;
  applyInfo?: string;
};

export type SampleApplyListRsp = {
  list: SampleApplyListItem[];
  total: number;
};

export type SampleOrderStatistics = {
  relatedOrderCount?: string;
  validOrderCount?: string;
  settledOrderCount?: string;
};

export type SampleThresholdOption = {
  value?: number;
  minValue?: string;
  label?: string;
};

export type SampleThresholdOptionsRsp = {
  gmvOptions: SampleThresholdOption[];
  fansOptions: SampleThresholdOption[];
};

export type SampleSetUpReq = {
  productId: string;
  enable: boolean;
  applyThresholdSalesRevenueIn30Days?: number | null;
  applyThresholdTalentFansNumber?: number | null;
  attrKeys?: string[];
  fulfillmentThresholdSettlementOrder?: string | null;
  fulfillmentThresholdLive?: string | null;
  fulfillmentThresholdVideo?: string | null;
};

/** 商品当前生效的寄样规则 */
export type SampleSetUpInfo = {
  productId?: string;
  enable?: boolean;
  applyThresholdSalesRevenueIn30Days?: number;
  applyThresholdTalentFansNumber?: number;
  /** 回显为 SKU 对象列表；设置接口提交时为 skuId 字符串数组 */
  attrKeys?: SampleSkuAttr[];
  fulfillmentThresholdSettlementOrder?: string;
  /** 是否开启直播履约：0 关，>0 开 */
  fulfillmentThresholdLive?: string;
  /** 是否开启短视频履约：0 关，>0 开 */
  fulfillmentThresholdVideo?: string;
};

/** 寄样规则默认门槛（展示用） */
export type SampleSetUpDefault = {
  /** 直播讲解时长，单位秒 */
  fulfillmentThresholdLive?: string;
  /** 短视频上传天数 */
  fulfillmentThresholdVideo?: string;
};

export type SampleSetUpGetRsp = {
  setting?: SampleSetUpInfo;
  default?: SampleSetUpDefault;
};

export type GetSampleSetUpReq = {
  productId: string;
};

/** 批量导入物流失败行 */
export type SampleApplyImportTrackingNoFailedItem = {
  /** Excel 行号，从 1 开始 */
  rowNumber?: number;
  sampleApplyId?: string;
  trackingNo?: string;
  reason?: string;
};

/** 批量导入物流响应 */
export type SampleApplyImportTrackingNoRsp = {
  /** 导入文件中的非空数据行数 */
  rowCount?: number;
  /** 成功更新的申请数（按申请ID去重） */
  updatedCount?: number;
  failedItems?: SampleApplyImportTrackingNoFailedItem[];
};

export const SETTLEMENT_STATUS_TEXT: Record<number, string> = {
  20: "未结算",
  100: "已结算",
  200: "取消结算",
};

/** 近 30 天销售额门槛文案（与 set-up 枚举一致） */
const SALES_THRESHOLD_LABEL: Record<number, string> = {
  0: "不限",
  1: "1万元以下",
  2: "1万元至5万元",
  3: "5万元至10万元",
  4: "10万元至20万元",
  5: "20万元至30万元",
  6: "30万元至40万元",
  7: "40万元至50万元",
  8: "50万元至100万元",
  9: "100万元至200万元",
  10: "200万元至300万元",
  11: "300万元至400万元",
  12: "400万元至500万元",
  13: "500万元至700万元",
  14: "700万元至1000万元",
  15: "1000万元以上",
};

/** 达人粉丝数门槛文案（与 set-up 枚举一致） */
const FANS_THRESHOLD_LABEL: Record<number, string> = {
  0: "不限",
  1: "1万以下",
  2: "1万至10万",
  3: "10万至50万",
  4: "50万以上",
};

/**
 * 本地快递公司识别（免 API）。
 * 仅按字母前缀匹配；纯数字单号号段易重叠，不猜测，归为「其他快递」。
 */
const LOGISTICS_DETECT_RULES: Array<{ name: string; test: (no: string) => boolean }> = [
  { name: "顺丰速运", test: (no) => /^(SF|SFE)/.test(no) },
  { name: "京东物流", test: (no) => /^(JD|JDKY)/.test(no) },
  { name: "圆通快递", test: (no) => /^(YT|YTO)/.test(no) },
  { name: "韵达快递", test: (no) => /^YD/.test(no) },
  { name: "极兔速递", test: (no) => /^(JT|JTEX)/.test(no) || /^[ABDE]\d{12}$/.test(no) },
  { name: "中通快递", test: (no) => /^ZTO/.test(no) },
  { name: "申通快递", test: (no) => /^STO/.test(no) },
  { name: "德邦快递", test: (no) => /^(DB|DBL)/.test(no) },
  { name: "百世快递", test: (no) => /^(HTKY|BEST)/.test(no) },
  { name: "EMS", test: (no) => /^EMS/.test(no) || /^[A-Z]{2}\d{9}CN$/.test(no) },
];

export function detectLogisticsCompany(trackingNo: string): string {
  const no = trackingNo.trim().toUpperCase().replace(/[\s-]/g, "");
  if (!no) return "其他快递";
  const matched = LOGISTICS_DETECT_RULES.find((rule) => rule.test(no));
  return matched?.name ?? "其他快递";
}

/** 展示用：unix 秒 → 时间文案 */
export function formatUnixSeconds(value?: string) {
  const num = Number(value);
  if (!value || !Number.isFinite(num) || num <= 0) return "";
  return dayjs.unix(num).format("YYYY-MM-DD HH:mm:ss");
}

/** 展示用：分 → 元 */
export function formatFenToYuan(value?: string | number) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return num / 100;
}

/** 展示用：价格区间（分） */
export function formatPriceRange(minPrice?: number, maxPrice?: number) {
  const min = minPrice == null ? undefined : minPrice / 100;
  const max = maxPrice == null ? undefined : maxPrice / 100;
  if (min == null && max == null) return "-";
  if (min == null) return String(max);
  if (max == null || min === max) return String(min);
  return `${min}-${max}`;
}

/** 展示用：销售额门槛 */
export function formatSalesThresholdLabel(raw?: string) {
  const code = Number(raw);
  if (!Number.isFinite(code)) return "不限";
  return SALES_THRESHOLD_LABEL[code] ?? String(raw);
}

/** 展示用：粉丝门槛 */
export function formatFansThresholdLabel(raw?: string) {
  const code = Number(raw);
  if (!Number.isFinite(code)) return "不限";
  return FANS_THRESHOLD_LABEL[code] ?? String(raw);
}

/** 是否存在需人工完成的履约（直播 / 短视频） */
export function hasManualFulfillment(fulfillment?: SampleFulfillmentInfo) {
  return Number(fulfillment?.thresholdLive) > 0 || Number(fulfillment?.thresholdVideo) > 0;
}

/** 寄样申请列表 */
export const getSampleApplyList = async (params: SampleApplyListReq = {}): Promise<SampleApplyListRsp> => {
  const res = await request<SampleApplyListRsp, SampleApplyListReq>(_POST, `${BASE}/apply/list`)(params);
  return {
    list: res.data?.list ?? [],
    total: res.data?.total ?? 0,
  };
};

/** 寄样申请操作日志：type=sample_apply */
export const listSampleApplyOperationLog = async (sampleApplyId: string) => {
  const res = await request<{ items?: SampleOperationLogItem[] }, { rowId: string; type: string }>(
    _POST,
    "/admin/operation/log/list"
  )({
    rowId: sampleApplyId,
    type: "sample_apply",
  });
  return res.data?.items ?? [];
};

/** 寄样申请详情 */
export const getSampleApplyDetail = async (sampleApplyId: string): Promise<SampleApplyDetail | undefined> => {
  const res = await request<SampleApplyDetail, { sampleApplyId: string }>(
    _POST,
    `${BASE}/apply/detail`
  )({ sampleApplyId });
  if (!res.data?.sampleApplyId) return undefined;
  return res.data;
};

/** 填写物流单号 */
export const fillLogistics = async (params: { sampleApplyId: string; trackingNo: string }) => {
  await request<Record<string, never>, { sampleApplyId: string; trackingNo: string }>(
    _POST,
    `${BASE}/apply/fill_tracking_no`
  )({
    sampleApplyId: params.sampleApplyId,
    trackingNo: params.trackingNo.trim(),
  });
  return { success: true, logisticsCompany: detectLogisticsCompany(params.trackingNo) };
};

/** 已完成的履约方式：1-直播履约，2-短视频履约，3-结算订单 */
export type SampleCompletedFulfillmentMethod = 1 | 2 | 3;

/** 寄样申请完成 */
export const completeFulfillment = async (params: {
  sampleApplyId: string;
  completedFulfillmentMethods: SampleCompletedFulfillmentMethod[];
}) => {
  await request<
    Record<string, never>,
    { sampleApplyId: string; completedFulfillmentMethods: SampleCompletedFulfillmentMethod[] }
  >(
    _POST,
    `${BASE}/apply/complete`
  )({
    sampleApplyId: params.sampleApplyId,
    completedFulfillmentMethods: params.completedFulfillmentMethods,
  });
  return { success: true };
};

/** 关联订单列表筛选状态：1-有效，2-已结算，0-不按状态过滤 */
export type SampleApplyOrderListStatus = 0 | 1 | 2;

/** 关联订单列表 */
export const getSampleApplyOrders = async (params: {
  sampleApplyId: string;
  page?: number;
  size?: number;
  status?: SampleApplyOrderListStatus;
}) => {
  const res = await request<{ list?: SampleApplyOrder[]; total?: number }, typeof params>(
    _POST,
    `${BASE}/apply/order/list`
  )({
    sampleApplyId: params.sampleApplyId,
    page: params.page ?? 1,
    size: params.size ?? 100,
    status: params.status,
  });
  return {
    list: res.data?.list ?? [],
    total: res.data?.total ?? 0,
  };
};

/** 关联订单统计 */
export const getSampleApplyOrderStatistics = async (sampleApplyId: string): Promise<SampleOrderStatistics> => {
  const res = await request<SampleOrderStatistics, { sampleApplyId: string }>(
    _POST,
    `${BASE}/apply/order/statistics`
  )({ sampleApplyId });
  return res.data ?? {};
};

/** 设置寄样规则 */
export const setSampleRule = (params: SampleSetUpReq) =>
  request<Record<string, never>, SampleSetUpReq>(_POST, `${BASE}/set-up`)(params);

/** 获取商品当前生效的寄样规则（设置页回显） */
export const getSampleSetUp = async (params: GetSampleSetUpReq): Promise<SampleSetUpGetRsp> => {
  const res = await request<SampleSetUpGetRsp, GetSampleSetUpReq>(_POST, `${BASE}/set-up/get`)(params);
  return {
    setting: res.data?.setting,
    default: res.data?.default,
  };
};

/** 获取寄样 GMV / 粉丝门槛选项 */
export const getSampleThresholdOptions = async (): Promise<SampleThresholdOptionsRsp> => {
  const res = await request<
    { gmvOptions?: SampleThresholdOption[]; fansOptions?: SampleThresholdOption[] },
    Record<string, never>
  >(
    _POST,
    `${BASE}/threshold/options`
  )({});
  return {
    gmvOptions: res.data?.gmvOptions ?? [],
    fansOptions: res.data?.fansOptions ?? [],
  };
};

/** 导出寄样申请列表 */
export const exportSampleApplyList = (params: SampleApplyListReq = {}) =>
  requestExport(`${BASE}/apply/export`, {
    method: _POST,
    params,
    callback: (url) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = `寄样管理导出_${Date.now()}.xlsx`;
      link.click();
    },
  });

/** 下载物流导入模板 */
export const downloadLogisticsTemplate = async () => {
  const worksheet = XLSX.utils.aoa_to_sheet([["申请ID", "物流单号"]]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "导入模板");
  XLSX.writeFile(workbook, "寄样物流单号导入模板.xlsx");
};

/** 批量导入物流单号 */
export const importLogistics = async (file: File): Promise<SampleApplyImportTrackingNoRsp> => {
  const formData = new FormData();
  formData.append("fileName", file);

  const res = await request<SampleApplyImportTrackingNoRsp, FormData>(_POST, `${BASE}/apply/import_tracking_no`, {
    "Content-Type": "multipart/form-data",
  })(formData);

  return res.data ?? {};
};

const sampleManage = {
  getSampleApplyList,
  getSampleApplyDetail,
  listSampleApplyOperationLog,
  fillLogistics,
  completeFulfillment,
  getSampleApplyOrders,
  getSampleApplyOrderStatistics,
  setSampleRule,
  getSampleSetUp,
  getSampleThresholdOptions,
  downloadLogisticsTemplate,
  importLogistics,
  exportSampleApplyList,
  detectLogisticsCompany,
};

export default sampleManage;
